/* eslint-disable @typescript-eslint/no-explicit-any */
import { F0, F1, SMap } from '@client/new/helpers/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChangeHandler,
  DefaultValues,
  FieldPath,
  FieldPathValue,
  FieldValues,
  RegisterOptions,
  SetValueConfig,
  useForm,
  UseFormRegisterReturn,
  UseFormReturn,
} from 'react-hook-form';
import { ZodSchema } from 'zod';
import { WorkoutVMActionMap } from './workout.viewModel';
import { Exercise } from './workouts.domain';
import { _noop } from '@client/new/helpers/general';
import { useEffect } from 'react';
import { toErrors } from '@client/new/helpers/forms';

export type FormVM = {
  formMap: SMap<UseFormReturn<FieldValues, any, FieldValues>>;
  addForm: <T extends FieldValues>(
    id: string,
    data: UseFormReturn<T, any, T>,
  ) => void;
  showErrors: boolean;
  getExercise: <T extends FieldValues>(id: string) => DefaultValues<T>;
  setExercise: (id: string, e: Exercise) => void;
  updateTile: (p: { id: string; title: string }) => void;
  markEdited: () => void;
  isValid: F0;
  getErrors: F0;
  subscribeOnMedia: (
    id: string,
    cb: F1<WorkoutVMActionMap['updateSubExerciseMedia']>,
  ) => void;
  getDuration: (id: string) => string;
  exerciseMap: SMap<Exercise>;
};
type UseFormRegister<TFieldValues extends FieldValues> = <
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  name: TFieldName,
  options?: RegisterOptions<TFieldValues, TFieldName> & { noValue?: boolean },
) => UseFormRegisterReturn<TFieldName> & {
  value?: FieldPathValue<TFieldValues, TFieldName>;
  error?: string;
};

type CreateUseVMRes<T extends FieldValues> = T &
  Pick<UseFormReturn<T>, 'setValue' | 'control' | 'watch'> &
  Pick<UseFormReturn<T>['formState'], 'isValid'> & {
    register: UseFormRegister<T>;
    tick: F0;
    errors: { title: string; messages: string[] } | null;
  };

export const createUseVM =
  <T extends FieldValues, T2 extends Exercise>(
    schema: ZodSchema<T>,
    toExercise: (data: T) => T2,
  ) =>
  (formVM: FormVM, id: string): CreateUseVMRes<T> => {
    const defaultValues = { ...formVM.getExercise<T>(id) };
    const createdForm = useForm<T>({
      defaultValues,
      resolver: zodResolver(schema),
    });

    let stored = formVM.formMap[id] as UseFormReturn<T> | undefined;

    const setValue = <TName extends FieldPath<T>>(
      name: TName,
      value: FieldPathValue<T, TName>,
      options?: SetValueConfig,
    ) => {
      formVM.markEdited();
      (stored || createdForm).setValue(name, value, options);
    };

    if (!stored) {
      formVM.addForm(id, createdForm as any);
      formVM.setExercise(id, toExercise(createdForm.getValues()));
      stored = createdForm;
      formVM.subscribeOnMedia(id, (p) => {
        setValue('media' as FieldPath<T>, p.media as FieldPathValue<T, never>);
      });
    }

    const form = stored;

    const tick = () => {
      formVM.markEdited();
      formVM.setExercise(id, toExercise(createdForm.getValues()));
    };

    useEffect(() => {
      const subscription = form.watch((value, { name }) => {
        if (!name || !name.startsWith('sets')) return;
        form.trigger(name);
        const vs = form.getValues();
        formVM.setExercise(id, toExercise(vs));
      });
      return () => subscription.unsubscribe();
    }, []);

    const register: UseFormRegister<T> = (name, options) => {
      const res = form.register(name, options);
      const onChange: ChangeHandler = (e) => {
        setValue(name, e.target.value, { shouldValidate: true });
        form.trigger(name);
        formVM.setExercise(id, toExercise(form.getValues()));
        if (name === 'name') formVM.updateTile({ id, title: e.target.value });
        return res.onChange(e);
      };

      return {
        ...res,
        ...(options?.noValue ? {} : { value: form.watch(name) }),
        onChange,
        error: form.getFieldState(name).error?.message,
      };
    };

    return {
      ...form.watch(),
      register,
      watch: form.watch,
      tick,
      control: form.control,
      setValue,
      errors: formVM.showErrors ? toErrors(form.formState.errors) : null,
      isValid: form.formState.isValid,
    };
  };

export const scrollToElement = (elementId: string, delay = 100) => {
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, delay);
};
